import React from "react"
import { twMerge } from "tailwind-merge"

import placeholderImage from "../../../../assets/images/profile-photo-placeholder.jpg"
import { groupEventCardDateLabel } from "../../../@core/bookableEventType/bookableEventOccurrence.utils"
import { formatBookableEventDurationLabel } from "../../../@core/bookableEventType/bookableEventType.utils"
import GroupEventCardMetaLine from "../../../@core/bookableEventType/GroupEventCardMetaLine"
import {
  FILESTACK_CDN_WIDTHS,
  ensureFilestackCdnResized,
  filestackUrlFromPhotoHandles
} from "../../../utils/filestackCdn"
import { getServicePriceRange, getServiceDurationRange, formatDurationWithSessions } from "../../../utils/utils"
import { getSectionColorScheme } from "../utils/colorModeUtils"
import { formatFontFamily, formatHeadingFontFamily } from "../utils/fontUtils"
import { normalizeServiceImageObjectFit } from "../utils/serviceCardImageUtils"
import { applyButtonTheme, getThemeShapeClasses, getThemeBorderRadius } from "../utils/themeUtils"
import { buildServiceUrl, buildPublicPageUrl, buildBookingUrl, buildEventTypeUrl } from "../utils/urlUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

import { ScrollRevealItem } from "./shared/ScrollReveal"
import {
  CARD_IMAGE_HOVER_CLASSES,
  CARD_IMAGE_OVERLAY_CLASSES,
  CARD_ROW_HOVER_CLASSES
} from "./shared/scrollRevealVariants"
import ServiceCardImage from "./shared/ServiceCardImage"

const FeaturedServicesSectionDesktop = ({ section, practice, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const layout = settings.layout || "double_img_r"
  const selectedServiceIds = settings.selectedServiceIds || []
  const imageObjectFit = normalizeServiceImageObjectFit(settings.imageObjectFit)

  // Get color scheme based on color mode
  const colorScheme = getSectionColorScheme(settings, theme)
  const { themeColors } = colorScheme

  // Use color scheme for colors
  const sectionBackgroundColor = colorScheme.sectionBackground
  const headingColor = colorScheme.headingColor
  const bodyColor = colorScheme.bodyColor

  const handleItemClick = (item) => {
    if (item.itemType === "service" || item.itemType === "package") {
      window.location.href = buildServiceUrl(item, practice)
    } else if (item.itemType === "event") {
      // For event instances, use the slug from bookableEventTypeSlug if available
      const eventSlug = item.bookableEventTypeSlug || item.slug
      window.location.href = buildEventTypeUrl(eventSlug, practice)
    }
  }

  // Get all available items
  // Services includes both services and packages (packages have package: true)
  const allServices = (practice?.services || []).filter((service) => service.public === true)
  const allPracticeServices = allServices.filter((service) => !service.package)
  const allPracticePackages = allServices.filter((service) => service.package === true)
  // Events can be either event types (templates) or specific event instances
  const allPracticeEventTypes = practice?.bookableEventTypes || []
  const allPracticeEventInstances = practice?.bookableEvents || []
  const allPracticeEvents = [...allPracticeEventTypes, ...allPracticeEventInstances]

  // Only show items that are explicitly selected (selectedItemIds)
  // Parse the selectedItemIds to handle the new unified format: "service-123", "package-456", "event-slug"
  const selectedItemIds = settings.selectedItemIds || selectedServiceIds || []
  const selectedItems = selectedItemIds
    .map((id) => {
      // Handle both old format (just ID) and new format (type-id)
      if (typeof id === "string" && id.includes("-")) {
        const [type, itemId] = id.split("-")
        if (type === "service") {
          const service = allPracticeServices.find((s) => s.id?.toString() === itemId)
          return service ? { ...service, itemType: "service" } : null
        }
        if (type === "package") {
          const pkg = allPracticePackages.find((p) => p.id?.toString() === itemId)
          return pkg ? { ...pkg, itemType: "package" } : null
        }
        if (type === "event") {
          // Events can have either numeric ID or slug
          const event = allPracticeEvents.find((e) => e.id?.toString() === itemId || e.slug === itemId)
          return event ? { ...event, itemType: "event" } : null
        }
        return null
      }
      // Old format: just the service ID
      const service = allPracticeServices.find((s) => s.id === id)
      return service ? { ...service, itemType: "service" } : null
    })
    .filter(Boolean)

  // Limit items based on layout
  const maxItems = layout === "list" ? 3 : layout === "highlights_2" ? 2 : layout === "highlights_3" ? 3 : 2
  const allItems = selectedItems.slice(0, maxItems)

  // Get images for double image layouts - use custom images from image selector
  const getDoubleImageUrls = () => {
    // For double image layouts, use only the selected images from the image picker
    const customImage1 = settings.image1Url
    const customImage2 = settings.image2Url

    return [customImage1 || placeholderImage, customImage2 || placeholderImage]
  }

  const imageUrls = getDoubleImageUrls()

  // CTA handling
  const showCTA = settings.showCTA !== false
  const ctaText = settings.ctaText || "Learn More"
  const ctaAction = settings.ctaAction || "page"
  const ctaTarget = settings.ctaTarget || ""

  const handleCTAClick = () => {
    const scrollToLayout = (layoutId) => {
      if (!layoutId) return
      const targetElement = document.querySelector(`[id^=section-${layoutId}-]`)
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: "smooth" })
      }
    }

    switch (ctaAction) {
      case "page": {
        if (ctaTarget) {
          window.location.href = buildPublicPageUrl(ctaTarget, practice)
        }
        break
      }
      case "service":
        if (ctaTarget) {
          window.location.href = buildServiceUrl(ctaTarget, practice)
        }
        break
      case "booking":
        window.location.href = buildBookingUrl(practice)
        break
      case "contact":
        scrollToLayout("contact")
        break
      case "scroll":
        window.scrollBy({ top: window.innerHeight, behavior: "smooth" })
        break
      case "external":
        if (ctaTarget) {
          const url = ctaTarget.startsWith("http") ? ctaTarget : `https://${ctaTarget}`
          window.open(url, "_blank")
        }
        break
      default:
        break
    }
  }

  // Render item card for highlights layout
  const renderHighlightCard = (item, index) => {
    // Get display info based on item type
    let price = ""
    let duration = ""
    let locationInfo = ""
    let displayName = item.name || item.title
    let displayDescription = item.description || ""

    if (item.itemType === "service") {
      price = getServicePriceRange(item, practice?.defaultCurrency || "USD")
      duration = getServiceDurationRange(item)
      const serviceType = item.virtual ? "Virtual" : "In-person"
      const location = practice?.location?.city ? `${practice.location.city}, ${practice.location.state}` : ""
      const locationType = item.atHome || item.at_home ? "At your home" : ""
      locationInfo = [serviceType, location, locationType].filter(Boolean).join(" • ")
    } else if (item.itemType === "package") {
      price = getServicePriceRange(item, practice?.defaultCurrency || "USD")
      const timeLength =
        typeof item.timeLength === "number" ? item.timeLength : item.timeLength?.value || item.timeLength
      duration = timeLength ? formatDurationWithSessions(timeLength, item) : ""
      locationInfo = practice?.location?.city ? `${practice.location.city}, ${practice.location.state}` : ""
    } else if (item.itemType === "event") {
      // Events send amount_cents, not price
      price = item.amount_cents ? `$${(item.amount_cents / 100).toFixed(2)}` : item.price ? `$${item.price}` : ""
      duration = formatBookableEventDurationLabel(item)
      const virtual = item.kind === "virtual" || item.virtual ? "Virtual" : "In-person"
      const capacity = item.capacity ? `${item.capacity} capacity` : ""
      locationInfo = [virtual, capacity].filter(Boolean).join(" • ")
    }

    // Get image URL from various possible sources
    // Check both camelCase (packages) and snake_case (events) properties
    const photoHandles = item.photoHandles || item.filestackPhoto || item.filestack_photo
    const imageUrl =
      ensureFilestackCdnResized(
        item.imageUrl ||
          item.mediumWebpUrl ||
          item.photoUrl ||
          item.photo_url ||
          filestackUrlFromPhotoHandles(photoHandles, { width: FILESTACK_CDN_WIDTHS.card }),
        { width: FILESTACK_CDN_WIDTHS.card }
      ) || placeholderImage

    if (displayDescription && displayDescription.length > 200) {
      displayDescription = displayDescription.substring(0, 200) + "..."
    }

    // Check if this is a package or group event (for tag display)
    const isPackage = item.itemType === "package" || item.type === "package"
    const isGroupEvent = item.type === "group_event" || item.itemType === "event"

    return (
      <ScrollRevealItem
        key={index}
        staggerIndex={index + 1}
        className="group flex w-full max-w-map-container flex-col gap-4">
        <div className={twMerge("relative aspect-[396/250] w-full overflow-hidden", getThemeShapeClasses(theme))}>
          <ServiceCardImage
            src={imageUrl}
            alt={displayName}
            objectFit={imageObjectFit}
            hoverClassName={CARD_IMAGE_HOVER_CLASSES}
            onError={(e) => {
              if (e.target.src !== placeholderImage) e.target.src = placeholderImage
            }}
          />
          <div className={CARD_IMAGE_OVERLAY_CLASSES} style={{ backgroundColor: "rgba(0,0,0,0.06)" }} />
        </div>

        <div className="flex flex-col gap-2">
          {/* Price + Title */}
          <div className="flex flex-col gap-0">
            {/* Price and Tag */}
            <div className="flex items-center gap-1.5">
              {price && (
                <span
                  className="text-xl font-semibold"
                  style={{
                    fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                    fontWeight: theme?.fonts?.paragraphBoldWeight || "600",
                    color: headingColor,
                    lineHeight: "32px"
                  }}>
                  {price}
                </span>
              )}
              {(isPackage || isGroupEvent) && (
                <div
                  className="rounded px-3 py-1"
                  style={{
                    backgroundColor: theme?.colors?.bgLight || themeColors.bgLight || "#F4ECE9",
                    borderRadius: theme?.borderRadius ? `${parseInt(theme.borderRadius) / 4}px` : "4px"
                  }}>
                  <span
                    className="text-xs font-semibold uppercase tracking-wide"
                    style={{
                      fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                      fontWeight: theme?.fonts?.paragraphBoldWeight || "600",
                      color: headingColor,
                      lineHeight: "16px"
                    }}>
                    {isPackage
                      ? item.numberOfSessions
                        ? `${item.numberOfSessions} Session Package`
                        : "Package"
                      : "Group Event"}
                  </span>
                </div>
              )}
            </div>
            {/* Title */}
            <h3
              className="text-[27px] font-bold leading-[40px]"
              style={{
                fontFamily: formatHeadingFontFamily(theme?.fonts?.heading),
                fontWeight: theme?.fonts?.headingWeight || "700",
                color: headingColor
              }}>
              {displayName}
            </h3>
          </div>

          {/* Duration and Location */}
          {item.itemType === "event" ? (
            <GroupEventCardMetaLine
              duration={duration}
              locationInfo={locationInfo}
              dateLabel={groupEventCardDateLabel(item, practice)}
              className="text-base"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: bodyColor,
                lineHeight: "24px"
              }}
            />
          ) : (
            (duration || locationInfo) && (
              <p
                className="text-base"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                  fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                  color: bodyColor,
                  lineHeight: "24px"
                }}>
                {duration && locationInfo ? `${duration} • ${locationInfo}` : duration || locationInfo}
              </p>
            )
          )}

          {/* Description - Limited to 2 lines */}
          {displayDescription && (
            <div
              className="line-clamp-2 text-lg"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: bodyColor,
                lineHeight: "32px",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden"
              }}
              dangerouslySetInnerHTML={{ __html: displayDescription }}
            />
          )}

          {/* Details button */}
          <button
            onClick={() => handleItemClick(item)}
            className="inline-flex w-auto items-center justify-center gap-3 self-start border-2 px-10 py-3 transition-opacity hover:opacity-90"
            style={applyButtonTheme(
              {
                backgroundColor: "#FFFFFF",
                borderColor: theme?.colors?.primary || "#BD654F",
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: theme?.colors?.primary || "#BD654F",
                fontSize: "16px",
                lineHeight: "24px"
              },
              theme
            )}>
            Details
          </button>
        </div>
      </ScrollRevealItem>
    )
  }

  // Render double image layout
  const renderDoubleImageLayout = (imagesOnRight) => {
    // Calculate the total height of the image group to properly center it
    // First image: starts at 0, height 309.498px, ends at 309.498px
    // Second image: starts at 199.056px, height 340.944px, ends at 540px
    // Total bounding box height: max(309.498, 540) = 540px
    const imageGroupHeight = 540

    // Calculate the total width needed for the images
    // For images on left: second image starts at 77.452px and has width 356.256px
    // Total width = 77.452px + 356.256px = 433.708px
    // For images on right: first image is at 233.196px with width 278.804px
    // Total width = 233.196px + 278.804px = 512px
    const imageGroupWidth = imagesOnRight ? 512 : 433.708

    return (
      <div
        className={twMerge("flex w-full items-center", imagesOnRight ? "gap-[104px]" : "justify-between gap-[52px]")}>
        {/* Images Section */}
        <div
          className={twMerge("relative shrink-0", imagesOnRight ? "order-2" : "order-1")}
          style={{
            height: `${imageGroupHeight}px`,
            width: `${imageGroupWidth}px`
          }}>
          <div className="relative h-full">
            {/* First image - positioned behind/offset */}
            {imageUrls[0] && (
              <div
                className="absolute"
                style={{
                  left: imagesOnRight ? "233.196px" : "0",
                  top: "0",
                  width: "278.804px",
                  height: "309.498px"
                }}>
                <img
                  src={imageUrls[0]}
                  alt=""
                  className={twMerge("size-full", getThemeShapeClasses(theme))}
                  style={{
                    objectPosition: "center",
                    transform: imagesOnRight ? "rotate(180deg) scaleY(-1)" : "none",
                    borderRadius: getThemeBorderRadius(theme)
                  }}
                  onError={(e) => {
                    if (e.target.src !== placeholderImage) e.target.src = placeholderImage
                  }}
                />
              </div>
            )}
            {/* Second image - positioned in front/overlapping */}
            {imageUrls[1] && (
              <div
                className="absolute"
                style={{
                  left: imagesOnRight ? "0" : "77.452px", // For left layout, offset second image to the right (356.256 - 278.804 = 77.452)
                  top: "199.056px",
                  width: "356.256px",
                  height: "340.944px"
                }}>
                <img
                  src={imageUrls[1]}
                  alt=""
                  className={twMerge("size-full overflow-hidden", getThemeShapeClasses(theme))}
                  style={{
                    objectPosition: "center"
                  }}
                  onError={(e) => {
                    if (e.target.src !== placeholderImage) e.target.src = placeholderImage
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Text Content Section */}
        <ScrollRevealItem
          staggerIndex={1}
          className={twMerge(
            "flex max-w-[500px] flex-1 flex-col justify-center gap-6",
            imagesOnRight ? "order-1" : "order-2"
          )}>
          {/* Title */}
          {settings.title && (
            <h2
              className="text-left text-5xl font-bold leading-[64px]"
              style={{
                fontFamily: formatHeadingFontFamily(theme?.fonts?.heading),
                fontWeight: theme?.fonts?.headingWeight || "700",
                color: headingColor
              }}>
              {settings.title}
            </h2>
          )}

          {/* Subtitle */}
          {settings.subtitle && (
            <p
              className="text-left text-xl leading-[32px]"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: bodyColor
              }}>
              {settings.subtitle}
            </p>
          )}

          {/* CTA Button */}
          {showCTA && (
            <button
              onClick={handleCTAClick}
              className="inline-flex w-auto items-center justify-center gap-3 self-start px-10 py-3 transition-opacity hover:opacity-90"
              style={applyButtonTheme(
                {
                  backgroundColor: theme?.colors?.primary || "#BD654F",
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                  fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                  color: theme?.colors?.bgLight || "#F4ECE9",
                  fontSize: "16px",
                  lineHeight: "24px"
                },
                theme
              )}>
              {ctaText}
            </button>
          )}
        </ScrollRevealItem>
      </div>
    )
  }

  // Render section header (title, subtitle and Browse Services button)
  const renderSectionHeader = () => {
    const title = settings.title || "Work with me"
    const subtitle = settings.subtitle || ""

    return (
      <ScrollRevealItem staggerIndex={0} className="mb-12 flex w-full items-start justify-between gap-10">
        <div className="flex flex-col gap-4">
          {/* Title */}
          <h2
            className="text-left text-5xl font-bold leading-[64px]"
            style={{
              fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
              fontWeight: theme?.fonts?.headingWeight || "700",
              color: headingColor
            }}>
            {title}
          </h2>
          {/* Subtitle */}
          {subtitle && (
            <p
              className="text-left text-xl leading-[32px]"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: bodyColor
              }}>
              {subtitle}
            </p>
          )}
        </div>

        {/* Browse Services Button */}
        <button
          onClick={() => (window.location.href = buildServiceUrl(null, practice))}
          className="inline-flex shrink-0 items-center justify-center gap-3 self-center px-10 py-3 transition-opacity hover:opacity-90"
          style={applyButtonTheme(
            {
              backgroundColor: theme?.colors?.primary || "#BD654F",
              fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
              fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
              color: theme?.colors?.bgLight || "#F4ECE9",
              fontSize: "16px",
              lineHeight: "24px"
            },
            theme
          )}>
          Browse Services
        </button>
      </ScrollRevealItem>
    )
  }

  // Render service card for list layout (horizontal row design)
  const renderListServiceCard = (item, index) => {
    const displayName = item.name || item.title
    const price = getServicePriceRange(item, practice?.defaultCurrency || "USD")
    const duration = getServiceDurationRange(item)
    const serviceType = item.virtual ? "Virtual" : "In-person"
    const location = practice?.location?.city ? `${practice.location.city}, ${practice.location.state}` : ""
    const locationType = item.atHome || item.at_home ? "At your home" : ""
    const locationInfo = [serviceType, location, locationType].filter(Boolean).join(" • ")

    // Get image URL from various possible sources
    // Check both camelCase (packages) and snake_case (events) properties
    const photoHandles = item.photoHandles || item.filestackPhoto || item.filestack_photo
    const imageUrl =
      ensureFilestackCdnResized(
        item.imageUrl ||
          item.mediumWebpUrl ||
          item.photoUrl ||
          item.photo_url ||
          filestackUrlFromPhotoHandles(photoHandles, { width: FILESTACK_CDN_WIDTHS.card }),
        { width: FILESTACK_CDN_WIDTHS.card }
      ) || placeholderImage

    const displayDescription =
      item.description && item.description.length > 200 ? item.description.substring(0, 200) + "..." : item.description

    // Check if this is a package or group event (for tag display)
    const isPackage = item.type === "package"
    const isGroupEvent = item.type === "group_event" || item.itemType === "event"

    return (
      <ScrollRevealItem
        key={index}
        staggerIndex={index + 1}
        className={twMerge("group relative w-full", getThemeShapeClasses(theme), CARD_ROW_HOVER_CLASSES)}
        style={{ border: `1px solid ${theme?.colors?.stroke || "#E6E6E6"}` }}>
        <div className="relative flex w-full flex-row items-center justify-start gap-8 px-10 py-8">
          {/* Photo + Content */}
          <div className="relative flex min-h-px flex-1 flex-row items-center justify-start gap-8 p-0">
            {/* Large Square Image */}
            <div
              className={twMerge("relative size-48 shrink-0 overflow-hidden bg-gray-100", getThemeShapeClasses(theme))}>
              <ServiceCardImage
                src={imageUrl}
                alt={displayName}
                objectFit={imageObjectFit}
                hoverClassName={CARD_IMAGE_HOVER_CLASSES}
                onError={(e) => {
                  if (e.target.src !== placeholderImage) e.target.src = placeholderImage
                }}
              />
              <div className={CARD_IMAGE_OVERLAY_CLASSES} style={{ backgroundColor: "rgba(0,0,0,0.06)" }} />
            </div>

            <div className="relative flex min-h-px flex-1 flex-col items-start justify-start gap-2 overflow-hidden p-0 text-left">
              {/* Price + Title */}
              <div className="relative flex w-full flex-col items-start justify-start p-0">
                {/* Price and Tag */}
                <div className="flex items-center gap-1.5">
                  {price && (
                    <div
                      className="relative text-[20px]"
                      style={{
                        fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                        fontWeight: theme?.fonts?.paragraphBoldWeight || "600",
                        color: headingColor,
                        lineHeight: "32px"
                      }}>
                      {price}
                    </div>
                  )}
                  {(isPackage || isGroupEvent) && (
                    <div
                      className="rounded px-3 py-1"
                      style={{
                        backgroundColor: theme?.colors?.bgLight || themeColors.bgLight || "#F4ECE9",
                        borderRadius: theme?.borderRadius ? `${parseInt(theme.borderRadius) / 4}px` : "4px"
                      }}>
                      <span
                        className="text-xs font-semibold uppercase tracking-wide"
                        style={{
                          fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                          fontWeight: theme?.fonts?.paragraphBoldWeight || "600",
                          color: headingColor,
                          lineHeight: "16px"
                        }}>
                        {isPackage
                          ? item.numberOfSessions
                            ? `${item.numberOfSessions} Session Package`
                            : "Package"
                          : "Group Event"}
                      </span>
                    </div>
                  )}
                </div>
                {/* Title */}
                <div
                  className="relative line-clamp-2 w-full text-[28px]"
                  style={{
                    fontFamily: formatHeadingFontFamily(theme?.fonts?.heading),
                    fontWeight: theme?.fonts?.headingWeight || "700",
                    color: headingColor,
                    lineHeight: "40px",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden"
                  }}>
                  {displayName}
                </div>
              </div>

              {/* Duration and Location */}
              {(duration || locationInfo) && (
                <div
                  className="relative w-full truncate text-[16px]"
                  style={{
                    fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                    fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                    color: bodyColor,
                    lineHeight: "24px"
                  }}>
                  {duration && locationInfo ? `${duration} • ${locationInfo}` : duration || locationInfo}
                </div>
              )}

              {/* Description */}
              {displayDescription && (
                <div
                  className="prose prose-lg relative line-clamp-2 w-full max-w-none text-[18px]"
                  style={{
                    fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                    fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                    color: bodyColor,
                    lineHeight: "32px",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden"
                  }}
                  dangerouslySetInnerHTML={{ __html: displayDescription }}
                />
              )}
            </div>
          </div>

          {/* Button */}
          <button
            onClick={() => handleItemClick(item)}
            className="relative inline-flex shrink-0 cursor-pointer flex-row items-center justify-center gap-3 border-2 px-12 py-3 transition-opacity hover:opacity-90"
            style={applyButtonTheme(
              {
                backgroundColor: "#FFFFFF",
                borderColor: theme?.colors?.primary || "#BD654F",
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: theme?.colors?.primary || "#BD654F",
                fontSize: "16px",
                lineHeight: "24px"
              },
              theme
            )}>
            Details
          </button>
        </div>
      </ScrollRevealItem>
    )
  }

  // Render list layout (horizontal row design)
  const renderListLayout = () => (
    <div className="flex w-full flex-col gap-10">
      {/* Section Header with Browse Services button */}
      {renderSectionHeader()}

      {/* Service Cards - Horizontal Rows */}
      <div className="flex flex-col gap-6">{allItems.map((item, index) => renderListServiceCard(item, index))}</div>
    </div>
  )

  // Render highlights layout
  const renderHighlightsLayout = () => (
    <div className="flex w-full flex-col gap-10">
      {/* Section Header with Browse Services button */}
      {renderSectionHeader()}

      {/* Service Cards Grid */}
      <div className={layout === "highlights_2" ? "grid grid-cols-2 gap-10" : "flex flex-row gap-10"}>
        {allItems.map((item, index) => renderHighlightCard(item, index))}
      </div>
    </div>
  )

  const isListLayout = layout === "list"
  const isDoubleImageLayout = layout === "double_img_r" || layout === "double_img_l"
  const isHighlightsLayout = layout === "highlights_2" || layout === "highlights_3"
  const isImagesOnRight = isDoubleImageLayout && layout === "double_img_r"

  return (
    <section
      className={twMerge("relative py-24", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
      style={{
        backgroundColor: sectionBackgroundColor
      }}>
      <div className={twMerge("mx-auto max-w-[1160px]", !isImagesOnRight ? "px-14" : "px-6")}>
        {isListLayout
          ? renderListLayout()
          : isDoubleImageLayout
          ? renderDoubleImageLayout(layout === "double_img_r")
          : isHighlightsLayout
          ? renderHighlightsLayout()
          : renderDoubleImageLayout(true)}
      </div>
    </section>
  )
}

export default FeaturedServicesSectionDesktop
